import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Lock,
  ShieldCheck,
  Eye,
  Trash,
  ClockClockwise,
  Storefront,
  FileText,
  Sparkle,
  Download,
  QrCode,
  WhatsappLogo,
  EnvelopeSimple,
} from "@phosphor-icons/react";
import { QRCodeSVG } from "qrcode.react";
import { api, formatApiError } from "../lib/api";

/* ────────────────────────────────────────────────────────────────────
   /checkpoint/:cardCode  ·  Customer-facing Checkpoint vault view.

   Lists all Curb Checkpoint documents bound to this card. To open
   or delete a document the customer re-enters their card password
   (security wall). No subscription. No third party data leakage.
   ──────────────────────────────────────────────────────────────────── */

const DOC_LABELS = {
  passport: "Passport",
  id: "ID",
  drivers_licence: "Driver's Licence",
  pdp: "PDP",
  police_clearance: "Police Clearance",
  graduation_certificate: "Graduation Certificate",
  other: "Other Document",
};

function freshness(reminderDate) {
  if (!reminderDate) return { state: "ok", label: "No expiry set", days: null };
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const expiry = new Date(reminderDate + "T00:00:00");
  const days = Math.floor((expiry - today) / 86400000);
  if (days < 0) return { state: "stale", label: `Stale · ${Math.abs(days)}d ago`, days };
  if (days <= 7) return { state: "soon", label: `${days}d until refresh`, days };
  return { state: "ok", label: `${days}d until refresh`, days };
}

export default function CheckpointCard() {
  const { cardCode } = useParams();
  const code = (cardCode || "").toUpperCase();

  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [err, setErr] = useState("");
  const [active, setActive] = useState(null); // currently-open document

  const reload = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const r = await api.get(`/cc/cardholders/${code}/checkpoints`);
      setItems(r.data?.items || []);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [code]);
  useEffect(() => {
    reload();
  }, [reload]);

  return (
    <div className="min-h-screen bg-neutral-100 text-neutral-900">
      <header className="bg-black text-white">
        <div className="max-w-3xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link
            to="/my-cards"
            className="inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.3em] uppercase text-white/70 hover:text-white"
            data-testid="checkpoint-card-back"
          >
            <ArrowLeft size={14} weight="bold" /> My Cards
          </Link>
          <div className="inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.3em] uppercase">
            <ShieldCheck size={14} weight="bold" /> CURB CHECKPOINT
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-10">
        <div className="font-mono text-[11px] tracking-[0.4em] uppercase text-neutral-500">
          Card · {code}
        </div>
        <h1 className="font-display text-4xl sm:text-5xl leading-tight mt-3">
          Your Checkpoint vault.
        </h1>
        <p className="mt-3 text-sm text-neutral-700 max-w-xl leading-relaxed">
          Documents bound to your card via Curb Checkpoint mints. Tap a
          document to open it (you&apos;ll re-enter your password). Stale
          documents need to be refreshed at the issuing business.
        </p>

        {loading ? (
          <div className="mt-10 font-mono text-xs text-neutral-500">
            Loading vault…
          </div>
        ) : err ? (
          <div className="mt-10 bg-red-50 border-2 border-red-600 text-red-800 p-4 text-sm">
            {err}
          </div>
        ) : items.length === 0 ? (
          <EmptyState />
        ) : (
          <ul className="mt-10 space-y-3" data-testid="checkpoint-list">
            {items.map((it) => (
              <DocRow
                key={it.mint_code}
                item={it}
                onOpen={() => setActive(it)}
              />
            ))}
          </ul>
        )}
      </main>

      {active && (
        <DocumentDialog
          item={active}
          cardCode={code}
          onClose={() => setActive(null)}
          onDeleted={() => {
            setActive(null);
            reload();
          }}
        />
      )}
    </div>
  );
}

function DocRow({ item, onOpen }) {
  const f = freshness(item.reminder_date);
  const accent =
    f.state === "stale" ? "#dc2626" : f.state === "soon" ? "#d97706" : "#15803d";
  return (
    <li
      className="bg-white border-2 border-black p-4 sm:p-5 flex items-center gap-4"
      data-testid={`checkpoint-row-${item.mint_code}`}
    >
      <div
        className="w-10 h-10 flex items-center justify-center text-white shrink-0"
        style={{ background: "#000" }}
      >
        <FileText size={20} weight="bold" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-mono text-[9px] tracking-widest uppercase text-neutral-500">
          {DOC_LABELS[item.doc_type] || item.doc_type}
        </div>
        <div className="font-display text-lg leading-tight mt-0.5 truncate">
          {item.doc_name}
        </div>
        <div className="text-xs text-neutral-600 mt-1 flex items-center gap-2 flex-wrap">
          <span className="inline-flex items-center gap-1">
            <Storefront size={11} weight="bold" /> {item.business}
          </span>
          <span className="text-neutral-300">·</span>
          <span className="inline-flex items-center gap-1" style={{ color: accent }}>
            <ClockClockwise size={11} weight="bold" /> {f.label}
          </span>
        </div>
      </div>
      <button
        onClick={onOpen}
        className="bg-black text-white px-3 py-2 font-mono text-[10px] tracking-[0.3em] uppercase inline-flex items-center gap-1 shrink-0"
        data-testid={`checkpoint-open-${item.mint_code}`}
      >
        <Eye size={12} weight="bold" /> Open
      </button>
    </li>
  );
}

function EmptyState() {
  return (
    <div className="mt-10 bg-white border-2 border-dashed border-neutral-300 p-8 text-center">
      <Sparkle size={28} weight="bold" className="mx-auto text-neutral-400" />
      <div className="font-display text-xl mt-3">No documents bound yet</div>
      <p className="text-sm text-neutral-600 mt-2 max-w-md mx-auto">
        Take this card to any Curb Crew partner — they&apos;ll scan a Curb
        Mint sticker and bind your document to this vault. Each binding
        takes about 30 seconds.
      </p>
    </div>
  );
}

function DocumentDialog({ item, cardCode, onClose, onDeleted }) {
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [doc, setDoc] = useState(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [presenting, setPresenting] = useState(false);

  // Verifier QR — anyone can scan it without auth. Points at the same
  // public VERIFIED badge page that the printed sticker would open.
  const verifierUrl = (() => {
    try {
      return `${window.location.origin}/cc/mint/${item.mint_code}`;
    } catch {
      return `https://curbcrew.co.za/cc/mint/${item.mint_code}`;
    }
  })();

  async function open() {
    setBusy(true);
    setErr("");
    try {
      const r = await api.post(`/cc/mints/${item.mint_code}/document`, {
        card_code: cardCode,
        card_password: password,
        include_b64: true,
      });
      setDoc(r.data);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setBusy(false);
    }
  }

  // Save a base64 JPEG to the user's downloads. Converts to a Blob first
  // because (a) Chrome/Safari block large data: URI downloads, and
  // (b) the <a> element must actually be in the DOM when .click() fires
  // — detached anchors silently no-op in every modern browser.
  function saveJpegBase64(b64, filename) {
    if (!b64) return;
    const bin = atob(b64);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    const blob = new Blob([bytes], { type: "image/jpeg" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 30_000);
  }

  async function downloadStamped() {
    setBusy(true);
    setErr("");
    try {
      const r = await api.post(`/cc/mints/${item.mint_code}/document`, {
        card_code: cardCode,
        card_password: password,
        stamped: true,
        include_b64: true,
      });
      saveJpegBase64(
        r.data.document_jpeg_b64,
        `${r.data.doc_type}_${item.mint_code}_verified.jpg`,
      );
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setBusy(false);
    }
  }

  async function del() {
    setBusy(true);
    setErr("");
    try {
      await api.delete(`/cc/mints/${item.mint_code}`, {
        data: { card_code: cardCode, card_password: password },
      });
      onDeleted();
    } catch (e) {
      setErr(formatApiError(e));
      setBusy(false);
    }
  }

  function download() {
    saveJpegBase64(
      doc?.document_jpeg_b64,
      `${doc?.doc_type}_${item.mint_code}.jpg`,
    );
  }

  // Share the STAMPED document to WhatsApp (the recipient gets a verified
  // image with the Curb Checkpoint stamp + verify QR burned in). On modern
  // phones the Web Share API surfaces WhatsApp in the share sheet with the
  // file pre-attached. On desktop / older browsers we save the image and
  // open wa.me so the user can attach it manually.
  async function shareWhatsApp() {
    setBusy(true);
    setErr("");
    try {
      const r = await api.post(`/cc/mints/${item.mint_code}/document`, {
        card_code: cardCode,
        card_password: password,
        stamped: true,
        include_b64: true,
      });
      const filename = `${r.data.doc_type}_${item.mint_code}_verified.jpg`;
      const docLabel = DOC_LABELS[r.data.doc_type] || r.data.doc_type;
      const issuer = r.data.business || "—";
      const text =
        `Verified Curb Checkpoint document\n` +
        `Document: ${docLabel}\n` +
        `Issued by: ${issuer}\n` +
        `Verify: ${verifierUrl}`;

      // Build a real File for Web Share API
      const bin = atob(r.data.document_jpeg_b64);
      const bytes = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
      const file = new File([bytes], filename, { type: "image/jpeg" });

      // Best path — mobile: native share sheet with WhatsApp + file attached.
      if (
        typeof navigator !== "undefined" &&
        navigator.canShare &&
        navigator.canShare({ files: [file] })
      ) {
        try {
          await navigator.share({
            files: [file],
            text,
            title: "Curb Checkpoint document",
          });
          return;
        } catch (shareErr) {
          // User dismissed the share sheet — silent.
          if (shareErr?.name === "AbortError") return;
          // Anything else: fall through to wa.me fallback below.
        }
      }

      // Fallback — desktop or unsupported: save the JPEG, then open wa.me.
      saveJpegBase64(r.data.document_jpeg_b64, filename);
      const waText = encodeURIComponent(
        `${text}\n\n(Image saved to your downloads — attach it in WhatsApp.)`,
      );
      window.open(`https://wa.me/?text=${waText}`, "_blank");
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setBusy(false);
    }
  }

  // Share the STAMPED document via Email. Same trick as WhatsApp — try the
  // Web Share API first (on mobile the share sheet includes Gmail / Mail /
  // Outlook with the file pre-attached). On desktop / unsupported browsers
  // we save the JPEG and open a mailto: with a pre-filled subject and body
  // so the user can attach it in one gesture.
  async function shareEmail() {
    setBusy(true);
    setErr("");
    try {
      const r = await api.post(`/cc/mints/${item.mint_code}/document`, {
        card_code: cardCode,
        card_password: password,
        stamped: true,
        include_b64: true,
      });
      const filename = `${r.data.doc_type}_${item.mint_code}_verified.jpg`;
      const docLabel = DOC_LABELS[r.data.doc_type] || r.data.doc_type;
      const issuer = r.data.business || "—";
      const subject = `Verified Curb Checkpoint document — ${docLabel}`;
      const body =
        `Verified Curb Checkpoint document\n\n` +
        `Document: ${docLabel}\n` +
        `Issued by: ${issuer}\n` +
        `Verify: ${verifierUrl}\n`;

      const bin = atob(r.data.document_jpeg_b64);
      const bytes = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
      const file = new File([bytes], filename, { type: "image/jpeg" });

      if (
        typeof navigator !== "undefined" &&
        navigator.canShare &&
        navigator.canShare({ files: [file] })
      ) {
        try {
          await navigator.share({
            files: [file],
            text: body,
            title: subject,
          });
          return;
        } catch (shareErr) {
          if (shareErr?.name === "AbortError") return;
          // fall through to mailto fallback
        }
      }

      // Fallback — desktop or unsupported: save the JPEG, then open mailto
      // with subject + body pre-filled so the user just picks a recipient
      // and drags the just-saved image into the compose window.
      saveJpegBase64(r.data.document_jpeg_b64, filename);
      const mailto =
        `mailto:?subject=${encodeURIComponent(subject)}` +
        `&body=${encodeURIComponent(
          `${body}\n(Image saved to your downloads — attach it to this email.)`,
        )}`;
      window.location.href = mailto;
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
      onClick={presenting ? undefined : onClose}
      data-testid="checkpoint-dialog"
    >
      {/* PRESENT MODE — full-screen verifier QR for an officer to scan */}
      {presenting && doc ? (
        <div
          className="bg-white border-2 border-black max-w-md w-full"
          onClick={(e) => e.stopPropagation()}
          data-testid="checkpoint-present"
        >
          <div className="bg-green-700 text-white px-5 py-4 flex items-center justify-between">
            <div className="inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.3em] uppercase">
              <ShieldCheck size={12} weight="bold" /> SHOW TO VERIFIER
            </div>
            <button
              onClick={() => setPresenting(false)}
              className="font-mono text-[10px] tracking-widest uppercase"
              data-testid="checkpoint-present-close"
            >
              close
            </button>
          </div>
          <div className="p-6 text-center">
            <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-neutral-500">
              {DOC_LABELS[doc.doc_type]}
            </div>
            <h2 className="font-display text-2xl mt-1 leading-tight">
              {doc.business || "Curb Checkpoint"}
            </h2>
            <p className="text-xs text-neutral-600 mt-3 leading-relaxed max-w-xs mx-auto">
              Hand your phone to the officer. They point their camera at
              this QR — their phone opens the public verification page on{" "}
              <strong>curbcrew.co.za</strong> showing fresh / stale and
              issuer.
            </p>
            <div className="mt-6 flex items-center justify-center">
              <div className="border-4 border-black p-3 bg-white">
                <QRCodeSVG
                  value={verifierUrl}
                  size={260}
                  level="M"
                  includeMargin={false}
                  data-testid="checkpoint-present-qr"
                />
              </div>
            </div>
            <div className="mt-5 font-mono text-[10px] text-neutral-500 break-all">
              {verifierUrl}
            </div>
            <div className="mt-5 grid grid-cols-2 gap-3 text-xs text-left max-w-xs mx-auto">
              <Info label="ISSUER" value={doc.business} />
              <Info label="REFRESH" value={doc.reminder_date} />
            </div>
            <button
              onClick={() => setPresenting(false)}
              className="mt-6 w-full border-2 border-black px-4 py-3 font-mono text-[10px] tracking-[0.3em] uppercase"
              data-testid="checkpoint-present-back"
            >
              ← back to document
            </button>
          </div>
        </div>
      ) : (
        <div
          className="bg-white border-2 border-black max-w-lg w-full max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="bg-black text-white px-5 py-4 flex items-center justify-between">
            <div className="inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.3em] uppercase">
              <ShieldCheck size={12} weight="bold" />
              {DOC_LABELS[item.doc_type]}
            </div>
            <button
              onClick={onClose}
              className="font-mono text-[10px] tracking-widest uppercase"
              data-testid="checkpoint-dialog-close"
            >
              close
            </button>
          </div>

        {!doc ? (
          <div className="p-6">
            <h3 className="font-display text-2xl leading-tight">
              Re-enter your card password
            </h3>
            <p className="text-xs text-neutral-600 mt-2">
              We require password re-auth every time a document is opened.
            </p>
            <label className="block mt-5">
              <div className="font-mono text-[9px] tracking-widest uppercase text-neutral-500">
                Card password
              </div>
              <div className="mt-1 flex items-center gap-2 border-b-2 border-neutral-300 focus-within:border-black">
                <Lock size={14} weight="bold" className="text-neutral-400" />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="flex-1 bg-transparent py-2 outline-none text-sm"
                  data-testid="checkpoint-dialog-password"
                  autoFocus
                />
              </div>
            </label>
            {err && (
              <div className="mt-4 bg-red-50 border-2 border-red-600 text-red-800 p-3 text-sm">
                {err}
              </div>
            )}
            <button
              onClick={open}
              disabled={!password || busy}
              className="mt-6 w-full bg-black text-white font-mono text-[10px] tracking-[0.3em] uppercase py-3 disabled:opacity-40"
              data-testid="checkpoint-dialog-open"
            >
              {busy ? "Opening…" : "Open document"}
            </button>
          </div>
        ) : (
          <div className="p-6">
            <img
              src={`data:image/jpeg;base64,${doc.document_jpeg_b64}`}
              alt={DOC_LABELS[doc.doc_type]}
              className="w-full border border-neutral-200"
              data-testid="checkpoint-dialog-image"
            />
            <div className="mt-5 grid grid-cols-2 gap-3 text-xs">
              <Info label="ISSUER" value={doc.business} />
              <Info label="DATE" value={doc.doc_date} />
              <Info label="REFRESH" value={doc.reminder_date} />
              <Info label="HASH" value={`${doc.sha256?.slice(0, 10)}…`} mono />
            </div>
            <div className="mt-5 grid grid-cols-1">
              <button
                onClick={() => setPresenting(true)}
                className="bg-green-700 text-white px-3 py-4 font-mono text-[11px] tracking-[0.3em] uppercase inline-flex items-center justify-center gap-2"
                data-testid="checkpoint-dialog-present"
                title="Hand your phone to a verifier — they scan the on-screen QR to confirm authenticity"
              >
                <QrCode size={14} weight="bold" /> Show verifier
              </button>
            </div>
            <div className="mt-3 grid grid-cols-2 gap-3">
              <button
                onClick={shareWhatsApp}
                disabled={busy}
                className="bg-[#25D366] text-white px-3 py-4 font-mono text-[11px] tracking-[0.3em] uppercase inline-flex items-center justify-center gap-2 disabled:opacity-40"
                data-testid="checkpoint-dialog-share-whatsapp"
                title="Send the stamped, verifiable copy to anyone via WhatsApp"
              >
                <WhatsappLogo size={14} weight="bold" />
                {busy ? "…" : "WhatsApp"}
              </button>
              <button
                onClick={shareEmail}
                disabled={busy}
                className="bg-blue-700 text-white px-3 py-4 font-mono text-[11px] tracking-[0.3em] uppercase inline-flex items-center justify-center gap-2 disabled:opacity-40"
                data-testid="checkpoint-dialog-share-email"
                title="Send the stamped, verifiable copy to anyone via Email"
              >
                <EnvelopeSimple size={14} weight="bold" />
                {busy ? "…" : "Email"}
              </button>
            </div>
            <div className="mt-3 grid grid-cols-3 gap-3">
              <button
                onClick={download}
                className="border-2 border-black px-3 py-3 font-mono text-[10px] tracking-[0.3em] uppercase inline-flex items-center justify-center gap-2"
                data-testid="checkpoint-dialog-download"
                title="Save the original document image to your device"
              >
                <Download size={12} weight="bold" /> Save
              </button>
              <button
                onClick={downloadStamped}
                disabled={busy}
                className="bg-black text-white px-3 py-3 font-mono text-[10px] tracking-[0.3em] uppercase inline-flex items-center justify-center gap-2 disabled:opacity-40"
                data-testid="checkpoint-dialog-download-stamped"
                title="Saves a copy with the Curb Checkpoint stamp burned in — for sharing with third parties"
              >
                <ShieldCheck size={12} weight="bold" /> Stamped
              </button>
              {!confirmingDelete ? (
                <button
                  onClick={() => setConfirmingDelete(true)}
                  className="border-2 border-red-600 text-red-700 px-3 py-3 font-mono text-[10px] tracking-[0.3em] uppercase inline-flex items-center justify-center gap-2"
                  data-testid="checkpoint-dialog-delete"
                >
                  <Trash size={12} weight="bold" /> Delete
                </button>
              ) : (
                <button
                  onClick={del}
                  disabled={busy}
                  className="bg-red-600 text-white px-3 py-3 font-mono text-[10px] tracking-[0.3em] uppercase inline-flex items-center justify-center gap-2 disabled:opacity-40"
                  data-testid="checkpoint-dialog-delete-confirm"
                >
                  <Trash size={12} weight="bold" />
                  {busy ? "…" : "Confirm"}
                </button>
              )}
            </div>
            {confirmingDelete && (
              <p className="mt-3 text-[11px] text-red-700">
                This permanently retires the sticker. Cannot be undone.
              </p>
            )}
            {err && (
              <div className="mt-4 bg-red-50 border-2 border-red-600 text-red-800 p-3 text-sm">
                {err}
              </div>
            )}
          </div>
        )}
        </div>
      )}
    </div>
  );
}

function Info({ label, value, mono }) {
  return (
    <div>
      <div className="font-mono text-[9px] tracking-widest uppercase text-neutral-400">
        {label}
      </div>
      <div className={`mt-0.5 ${mono ? "font-mono text-[11px]" : "text-sm"}`}>
        {value || "—"}
      </div>
    </div>
  );
}
